import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { extendedAuthoredReach, legalAreaReach, projectileFlightBudget } from '../../../../shared/combat/reach';
import type { AbilityMetadata } from './AbilityRegistry';

export function authoredReach(value: number, canonical?: number, projectile = false): number {
  return value === canonical ? value : extendedAuthoredReach(value, projectile);
}

/** One build-time pass, so shared metadata and behavior configs cannot double-scale. */
export function tuneAbilityReach(metadata: Partial<AbilityMetadata>, shapes: {
  projectile?: { speed: number; lifespan?: number } | null;
  aoe?: { radius: number } | null;
  melee?: { range?: number } | null;
  cone?: { distance: number } | null;
  line?: { length: number } | null;
  nova?: { speed?: number; lifespan?: number } | null;
  aura?: { radius: number; damagePerTick?: unknown; affectsEnemies?: boolean } | null;
  summon?: { radius: number; damage?: unknown } | null;
}): void {
  if (metadata.reachTuned) return;
  const data = getAbilityData(metadata.id ?? '');
  const projectile = Boolean(metadata.projectile || shapes.projectile);
  if (metadata.range !== undefined) metadata.range = authoredReach(metadata.range, data?.range, projectile);
  if (shapes.melee?.range !== undefined) {
    shapes.melee.range = authoredReach(shapes.melee.range, data?.range);
    if (metadata.melee) metadata.melee.hitRange = metadata.melee.arcRadius = shapes.melee.range;
  }
  const area = (value: number, canonical = data?.radius): number => legalAreaReach(authoredReach(value, canonical), data);
  if (shapes.aoe) shapes.aoe.radius = area(shapes.aoe.radius);
  if (shapes.cone) shapes.cone.distance = area(shapes.cone.distance, data?.range);
  // BehaviorRegistry sends length as the line's radius and offsets its origin by length/2.
  // Keep that geometry and its authored width; the wire has no rectangular line envelope.
  if (shapes.line) shapes.line.length = area(shapes.line.length, data?.range);
  const combatAura = data?.abilityType === 'offensive' || data?.abilityType === 'debuff';
  const damagingAura = Boolean(shapes.aura?.damagePerTick) && shapes.aura?.affectsEnemies !== false;
  if (combatAura && shapes.aura && damagingAura) shapes.aura.radius = area(shapes.aura.radius);
  if (combatAura && shapes.summon && (shapes.summon.damage || damagingAura)) shapes.summon.radius = area(shapes.summon.radius);
  if (metadata.groundTarget) {
    metadata.groundTarget.maxRange = authoredReach(metadata.groundTarget.maxRange, data?.range);
    if (metadata.groundTarget.indicatorRadius !== undefined) metadata.groundTarget.indicatorRadius = area(metadata.groundTarget.indicatorRadius);
  }
  const reach = Math.max(metadata.range ?? 0, data?.range ?? 0);
  const p = metadata.projectile;
  if (p) {
    p.lifespan = projectileFlightBudget(reach, p.speed ?? 800, p.lifespan ?? 3000, p.homing?.delay ?? 0);
    if (shapes.projectile) shapes.projectile.lifespan = p.lifespan;
  }
  if (shapes.nova) shapes.nova.lifespan = projectileFlightBudget(reach, shapes.nova.speed ?? 800, shapes.nova.lifespan ?? 800);
  metadata.reachTuned = true;
}
